import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type ClientListItem = {
  id: number
  full_name: string
  medical_record_number: string
}

type InsuranceCoverage = {
  id: number
  payer_name: string
  member_id: string
  is_primary: boolean
}

type ClientDetail = ClientListItem & {
  insurance_coverages?: InsuranceCoverage[]
}

const SERVICE_CODES = [
  { value: '97151', label: '97151 — Behavior Assessment' },
  { value: '97152', label: '97152 — Assessment Supporting' },
  { value: '97153', label: '97153 — Adaptive Behavior Treatment (1:1)' },
  { value: '97154', label: '97154 — Group Adaptive Treatment' },
  { value: '97155', label: '97155 — Protocol Modification (BCBA)' },
  { value: '97156', label: '97156 — Caregiver Training (1 caregiver)' },
  { value: '97157', label: '97157 — Multi-Caregiver Training' },
  { value: '97158', label: '97158 — Group Caregiver Training' },
  { value: 'H0032', label: 'H0032 — Mental Health Supervision' },
]

const schema = z.object({
  client: z.coerce.number().int().positive('Client is required'),
  insurance_coverage: z.coerce.number().int().positive('Insurance coverage is required'),
  authorization_number: z.string().min(1, 'Authorization number is required'),
  service_code: z.string().min(1, 'Service code is required'),
  authorized_units: z.coerce.number().positive('Authorized units must be > 0'),
  unit_type: z.enum(['hours', 'units', 'sessions']),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().min(1, 'End date is required'),
  internal_notes: z.string().optional().or(z.literal('')),
})

type FormData = z.infer<typeof schema>

export default function AuthorizationCreatePage() {
  const navigate = useNavigate()
  const tenantSlug = resolveTenantSlug()
  const [clientSearch, setClientSearch] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const sixMonthsOut = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + 6)
    return d.toISOString().slice(0, 10)
  }, [])

  const clientsQuery = useOfflineFirstQuery<{ results: ClientListItem[] }>({
    queryKey: ['clients', { search: clientSearch }],
    cacheKey: `tenant:${tenantSlug ?? 'unknown'}:clients:auth_pick:q=${clientSearch.trim().toLowerCase() || 'all'}`,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<{ results: ClientListItem[] }>('/api/v1/clients/', {
        params: clientSearch.trim() ? { search: clientSearch.trim() } : {},
      })
      return res.data
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      client: 0,
      insurance_coverage: 0,
      authorization_number: '',
      service_code: '97153',
      authorized_units: 0,
      unit_type: 'units',
      start_date: today,
      end_date: sixMonthsOut,
      internal_notes: '',
    },
  })

  const clientIdNum = Number(watch('client')) || 0

  const clientDetailQuery = useOfflineFirstQuery<ClientDetail>({
    queryKey: ['client', clientIdNum, 'detail-for-auth'],
    cacheKey: `tenant:${tenantSlug ?? 'unknown'}:client:${clientIdNum}:detail-auth`,
    cacheMaxAgeMs: 5 * 60 * 1000,
    enabled: clientIdNum > 0,
    queryFn: async () => {
      const res = await apiClient.get<ClientDetail>(`/api/v1/clients/${clientIdNum}/`)
      return res.data
    },
  })
  const selectedClient = clientDetailQuery.data ?? null
  const coverages = selectedClient?.insurance_coverages ?? []

  // Auto-pick the primary coverage
  useEffect(() => {
    if (!coverages.length) return
    const primary = coverages.find((c) => c.is_primary) ?? coverages[0]
    setValue('insurance_coverage', primary.id, { shouldValidate: true })
  }, [coverages, setValue])

  // Auto-select client on single match
  useEffect(() => {
    const r = clientsQuery.data?.results ?? []
    if (clientSearch.trim() && r.length === 1 && r[0].id !== clientIdNum) {
      setValue('client', r[0].id, { shouldValidate: true })
    }
  }, [clientSearch, clientsQuery.data?.results, clientIdNum, setValue])

  const onSubmit = async (data: FormData) => {
    setSubmitError(null)
    try {
      const res = await apiClient.post<{ id: number }>('/api/v1/authorizations/', {
        ...data,
        internal_notes: data.internal_notes || '',
      })
      navigate(`/app/authorizations/${res.data.id}`)
    } catch (err) {
      setSubmitError(getErrorMessage(err))
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">New Authorization</h1>
        <p className="text-sm text-gray-600 mt-1">
          Capture an insurance authorization. Sessions logged against this auth will deduct units automatically.
        </p>
      </div>

      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{submitError}</div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-5">
        <div>
          <label className="label" htmlFor="clientSearch">Find client</label>
          <input
            id="clientSearch"
            className="input mt-1"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            placeholder="Search by name or MRN"
          />
          {clientSearch.trim() && clientsQuery.data && (
            <p className="mt-1 text-xs text-gray-500">
              {(() => {
                const n = (clientsQuery.data.results ?? []).length
                if (n === 0) return 'No matches.'
                if (n === 1) return '1 match — auto-selected below.'
                return `${n} matches — choose from the dropdown below.`
              })()}
            </p>
          )}
        </div>

        <div>
          <label className="label" htmlFor="client">Client *</label>
          <select id="client" className="input mt-1" {...register('client')}>
            <option value="0">Select client</option>
            {(clientsQuery.data?.results ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name} (MRN {c.medical_record_number})
              </option>
            ))}
          </select>
          {errors.client && <p className="mt-1 text-sm text-red-600">{errors.client.message}</p>}
        </div>

        <div>
          <label className="label" htmlFor="insurance_coverage">Insurance coverage *</label>
          {coverages.length === 0 ? (
            <p className="mt-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              {clientIdNum > 0
                ? "This client has no insurance coverage on file. Add one on the client's record before creating an authorization."
                : 'Select a client first to see their insurance coverages.'}
            </p>
          ) : (
            <select id="insurance_coverage" className="input mt-1" {...register('insurance_coverage')}>
              <option value="0">Select coverage</option>
              {coverages.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.payer_name} — Member ID {c.member_id}{c.is_primary ? ' (primary)' : ''}
                </option>
              ))}
            </select>
          )}
          {errors.insurance_coverage && (
            <p className="mt-1 text-sm text-red-600">{errors.insurance_coverage.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label" htmlFor="authorization_number">Authorization number *</label>
            <input
              id="authorization_number"
              className="input mt-1"
              placeholder="e.g. AUTH-2026-001234"
              {...register('authorization_number')}
            />
            {errors.authorization_number && (
              <p className="mt-1 text-sm text-red-600">{errors.authorization_number.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="service_code">Service code *</label>
            <select id="service_code" className="input mt-1" {...register('service_code')}>
              {SERVICE_CODES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label" htmlFor="authorized_units">Authorized units *</label>
            <input
              id="authorized_units"
              type="number"
              step="0.25"
              min="0"
              className="input mt-1"
              {...register('authorized_units')}
            />
            {errors.authorized_units && (
              <p className="mt-1 text-sm text-red-600">{errors.authorized_units.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="unit_type">Unit type</label>
            <select id="unit_type" className="input mt-1" {...register('unit_type')}>
              <option value="units">Units (15 min)</option>
              <option value="hours">Hours</option>
              <option value="sessions">Sessions</option>
            </select>
          </div>

          <div>
            <label className="label" htmlFor="start_date">Start date *</label>
            <input id="start_date" type="date" className="input mt-1" {...register('start_date')} />
            {errors.start_date && <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>}
          </div>
        </div>

        <div>
          <label className="label" htmlFor="end_date">End date *</label>
          <input id="end_date" type="date" className="input mt-1" {...register('end_date')} />
          {errors.end_date && <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>}
          <p className="mt-1 text-xs text-gray-500">
            Most ABA authorizations cover a 6-month period. The platform will warn you 30 days before expiration.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="internal_notes">Internal notes</label>
          <textarea
            id="internal_notes"
            rows={3}
            className="input mt-1"
            placeholder="Reference numbers, payer-specific quirks, modifier requirements, etc."
            {...register('internal_notes')}
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
          <button type="button" className="btn-secondary" onClick={() => navigate('/app/authorizations')}>
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Saving…' : 'Create authorization'}
          </button>
        </div>
      </form>
    </div>
  )
}
